"use strict";

/**
 * Show preroll Ad on video load. It's called from the video.js
 * 
 * @example
 * 
 * ```js
 * if FlixPreroll.isShouldShow() {
 *      FlixPreroll.show();
 * }
 * ```
 * 
 * @see FlixPreroll.show to start debugging
 * @see FlixPreroll.isShouldShow to check when preroll should be shown
 */
var FlixPreroll = function () {
  var FlixPreroll = {};

  /**
   * Hold Plyr instance to make it easier to destroy later
   */
  var player;
  /**
   * If preroll or video (if preroll failed to load) should start playing automatically
   */
  var autoPlay = false;
  /**
   * track if ad was already triggered
   * @type {boolean}
   */
  var adTriggered = false;
  /**
   * Stop cookie name
   */
  var stopCookie = 'preroll_stop';
  /**
   * Preroll sessionStorage key
   */
  var sessionKey = 'preroll_session';
  /**
   * We set max amount of retries
   * @type {number}
   */
  var max_retries = 3;
  /**
   * Current retry count
   */
  var retry = 0;
  /**
   * load test vast
   */
  var testRun = 0;

  /**
   * Basically this is the preroll configuration. Returns VAST link to parse
   * @returns string
   */
  var getVastLink = function getVastLink() {
    // return 'https://eu-adsrv-cached.rtbsuperhub.com/vast/?placement=237b2fa2-cb71-419a-a378-a68ba24e82b9'; #test CTA BUTTON
    if (FlixUtils.isLocalEnv() && testRun) {
      return getTestVastLinks();
    }
    if (Flix.isTna()) {
      if (!Flix.ads.isOrganic()) {
        return "https://ad.twinrdengine.com/adraw?zone=01DXF6DT004000000000001XWG&tid=".concat(Flix.ads.iid, "&kw=").concat(Flix.ads.tags);
      }
      if (FlixUtils.isMobile()) {
        return "https://ad.twinrdengine.com/adraw?zone=01DXF6DT004000000000001TAM&tid=".concat(Flix.ads.iid, "&kw=").concat(Flix.ads.tags);
      } else {
        return "https://ad.twinrdengine.com/adraw?zone=01DXF6DT0040000000000019Q1&tid=".concat(Flix.ads.iid, "&kw=").concat(Flix.ads.tags);
      }
    } else {
      return "https://ad.twinrdengine.com/adraw?zone=01DXF6DT0040000000000019Q7&tid=".concat(Flix.ads.iid, "&kw=").concat(Flix.ads.tags);
    }
  };

  /**
   * Test different VAST scenarios with this Google links.
   * Use it only in `local` environment.
   * @see https://developers.google.com/interactive-media-ads/docs/sdks/html5/client-side/tags
   * @returns {string}
   */
  var getTestVastLinks = function getTestVastLinks() {
    return '/assets/m3u8_vast_test.xml?a'; // HLS VAST example
    // return 'https://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/124319096/external/single_ad_samples&ciu_szs=300x250&impl=s&gdfp_req=1&env=vp&output=vast&unviewed_position_start=1&cust_params=deployment%3Ddevsite%26sample_ct%3Dlinear&correlator=';
    return 'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/simid&description_url=https%3A%2F%2Fdevelopers.google.com%2Finteractive-media-ads&tfcd=0&npa=0&sz=640x480&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=';
  };

  /**
   * Check if Preroll should be shown
   * @returns {boolean}
   */
  FlixPreroll.isShouldShow = function () {
    if (!Flix.ads.enabled || FlixUtils.isCrawler()) {
      return false;
    }
    if (Flix.ads.isOrganic()) {
      return !FlixUtils.hasCookie(stopCookie) && FlixVideo.getViewCount() >= (Flix.isTna() ? 0 : 1); // show from the 2nd video (it means 1 video was already viewed)
    } else {
      return !FlixUtils.hasCookie(stopCookie) || !sessionStorage.getItem(sessionKey); // OR dont have session cookie (it means show preroll on every new session for mediabuy)
    }
  };

  /**
   * Main method that show preroll
   * @param {boolean} withAutoPlay
   */
  FlixPreroll.show = function () {
    var withAutoPlay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    autoPlay = withAutoPlay;
    adTriggered = false;
    Vast.prepare(getVastLink()).then(setupPlayer).then(Events.setupEvents) // skip, click, tracking, etc
    .then(showPlayer)["catch"](Events.onError);
  };

  /**
   * API method to call from other places like FPI
   */
  FlixPreroll.play = function () {
    if (player) {
      player.play();
    }
  };

  /**
   * API method to call from other places like FPI
   */
  FlixPreroll.afterSkip = function () {
    removePrerollAndShowVideo(false);
  };

  /**
   * Add video HTML to the document and initialize player with VAST events
   * @returns {Promise}
   */
  var setupPlayer = function setupPlayer() {
    return new Promise(function (resolve) {
      FlixPreroll.clearPlayer();
      var poster = document.querySelector('#video-player').getAttribute('poster');
      /** Sources for Hls will be set later in the code */
      var src = Vast.config.videoUrl.endsWith('.m3u8') || Vast.config.videoType != 'mp4' ? '' : "src=\"".concat(Vast.config.videoUrl, "\"");
      var html = "<video id=\"preroll-video-element\" ".concat(src, " poster=\"").concat(poster, "\" class=\"d-none\" playsinline></video>\n            <a nopop=\"1\" rel=\"nofollow\" class=\"d-none\" target=\"_blank\" id=\"prerollAdLink\">").concat(Vast.config.brandName, "</a>\n            <div class=\"d-none\" id=\"prerollAdSkip\">Skip Ad in ").concat(Vast.config.skipOffset, "</div>\n            <div class=\"d-none\" id=\"prerollAdCta\"><span style=\"cursor: pointer;\"></span></div>\n<!--            <a nopop=\"1\" rel=\"nofollow\" class=\"d-none btn btn-primary\" target=\"_blank\" id=\"visit-ad-site\">Visit Website</a>-->");
      document.querySelector('#preroll-container').innerHTML = html;

      // https://github.com/sampotts/plyr#options
      player = new Plyr('#preroll-video-element', {
        controls: ['play-large' // The large play button in the center
        ],
        settings: [],
        keyboard: {
          focused: false,
          global: false
        },
        fullscreen: {
          enabled: false,
          fallback: false,
          iosNative: true,
          container: null
        }
      });
      player.speed = 1;
      resolve();
    });
  };

  /**
   * Hide placeholder and show preroll player.
   * Also parse Hls if needed.
   */
  var showPlayer = function showPlayer() {
    var _document$querySelect, _document$querySelect2;
    /** Hide video player with placeholder and show preroll */
    (_document$querySelect = document.querySelector('#video-player-container')) === null || _document$querySelect === void 0 || _document$querySelect.classList.add('d-none');
    (_document$querySelect2 = document.querySelector('#preroll-video-element')) === null || _document$querySelect2 === void 0 || _document$querySelect2.classList.remove('d-none');
    if (Vast.config.videoUrl.endsWith('.m3u8') || Vast.config.videoType != 'mp4') {
      if (typeof Hls === 'undefined') {
        /** Load Hls script to the <head> */
        var s = document.createElement('script');
        s.onload = function () {
          return prepareHLS();
        };
        s.src = '/assets/libs/hls.light.min.js';
        document.head.appendChild(s);
      } else {
        /** If Hls already loaded on previous pages */
        prepareHLS();
      }

      // // If there is native support (mostly Safari), then use it
      // if (player.canPlayType('application/vnd.apple.mpegurl')) {
      //     player.src = Vast.config.videoUrl;
      //     player.play();
      // } else { // otherwise use Hls lib
      //     if (typeof Hls === 'undefined') {
      //         /** Load Hls script to the <head> */
      //         let s = document.createElement('script');
      //         s.onload = () => prepareHLS();
      //         s.src = '/assets/libs/hls.light.min.js';
      //         document.head.appendChild(s);
      //     } else {
      //         /** If Hls already loaded on previous pages */
      //         prepareHLS();
      //     }
      // }
    } else if (autoPlay) {
      // For default mp4 and `autoPlay` = true
      FlixPreroll.play();
    }
  };

  /**
   * Try to load hls media
   */
  var prepareHLS = function prepareHLS() {
    if (Hls.isSupported()) {
      var hls = new Hls();
      hls.loadSource(Vast.config.videoUrl);
      hls.attachMedia(player.media);
      hls.on(Hls.Events.MANIFEST_PARSED, function () {
        if (autoPlay) {
          FlixPreroll.play();
        }
      });
      hls.on(Hls.Events.ERROR, function () {
        Events.onError('HLS cannot be loaded');
      });
    } else {
      Events.onError('HLS not supported');
    }
  };

  /**
   * Destroy Plyr instance
   */
  FlixPreroll.clearPlayer = function () {
    if (typeof Plyr != 'undefined' && player instanceof Plyr) {
      player.destroy();
      player = null;
    }
  };

  /**
   * Destroy preroll player and show normal video player instead
   * @param {boolean} autoPlayMainVideo
   */
  var removePrerollAndShowVideo = function removePrerollAndShowVideo() {
    var _document$querySelect3;
    var autoPlayMainVideo = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    FlixPreroll.clearPlayer();
    if (document.querySelector('#preroll-container')) {
      document.querySelector('#preroll-container').innerHTML = '';
    }
    (_document$querySelect3 = document.querySelector('#video-player-container')) === null || _document$querySelect3 === void 0 || _document$querySelect3.classList.remove('d-none');
    FlixVideo.initPlayer(autoPlayMainVideo);
  };

  /**
   * Set cookie indicates thata preroll has been played.
   * And set sessionStorage item for mediabuys
   */
  var setStopCookie = function setStopCookie() {
    FlixUtils.setCookie(stopCookie, 1, Flix.ads.isOrganic() ? 1 / 2 : 1 / 4);
    sessionStorage.setItem(sessionKey, 1);
  };

  /**
   * VAST event handlers. Sending tracking urls, clicking on Skip or Ad link
   */
  var Events = function () {
    var Events = {};
    var skipActivated = false;
    var skipButtonInterval;

    /**
     * Setup all VAST event handlers
     * @returns {Promise}
     */
    Events.setupEvents = function () {
      return new Promise(function (resolve, reject) {
        skipActivated = false; // reset this variable

        clearInterval(skipButtonInterval);
        skipButtonInterval = null;
        document.querySelector('#prerollAdLink').setAttribute('href', Vast.config.clickThroughUrl);
        if (FlixUtils.isMobile()) {
          document.querySelector('#prerollAdCta span').classList.add('span');
        }
        document.querySelector('#prerollAdCta').addEventListener('click', function (elem) {
          if (adTriggered) {
            setTimeout(function () {
              return window.open(Vast.config.clickThroughUrl);
            }, 200);
            sendUrlEventsOnce(Vast.config.trackingLinks.clickTrackingUrl);
            player.pause();
          } else {
            onAdClick();
          }
        });
        document.querySelector('#prerollAdLink').addEventListener('click', function (e) {
          e.preventDefault(); //we dont allow user to click on that link, open the tab
          if (adTriggered) {
            setTimeout(function () {
              return window.open(Vast.config.clickThroughUrl);
            }, 200);
            sendUrlEventsOnce(Vast.config.trackingLinks.clickTrackingUrl);
            player.pause();
          } else {
            onAdClick();
          }
        });
        // document.querySelector('#visit-ad-site').setAttribute('href', Vast.config.clickThroughUrl);

        // player.on('error', reject);
        player.on('error', function () {
          Events.onError();
        });
        player.on('ready', function () {
          var height = document.querySelector('#preroll-container').clientHeight;
          if (height) {
            document.querySelector('#preroll-video-element').style.height = height + 'px';
          }
        });
        player.on('play', onPlay);
        // player.on('pause', FlixPreroll.play);
        player.on('ended', onEnded);
        player.on('timeupdate', onTimeupdate);
        resolve();
      });
    };

    /**
     * Ad link click event
     */
    var onAdClick = function onAdClick() {
      if (adTriggered) {
        return;
      }
      sendUrlEventsOnce(Vast.config.trackingLinks.clickTrackingUrl);
      // onSkipClick(false);
      player.pause();
      adTriggered = true;
      setTimeout(function () {
        return window.open(Vast.config.clickThroughUrl);
      }, 200);
    };

    /**
     * Player event triggered on play
     */
    var onPlay = function onPlay() {
      if (adTriggered) {
        return;
      }
      if (Flix.requireAgeVerification && !Flix.ageVerified) {
        player.pause();
        Flix.ads.enabled = false;
        FlixUtils.initAgeVerify();
        return;
      }

      // No pauses, nothing. Only Ads
      player.on('click', onAdClick);

      // Send impression requests
      sendUrlEventsOnce(Vast.config.trackingLinks.impressionUrl);
      setupSkipButton();
    };

    /**
     * Skip Ad button click event
     * @param {boolean} autoPlayMainVideo 
     */
    var onSkipClick = function onSkipClick() {
      var autoPlayMainVideo = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      setStopCookie();
      //we show that Fpi was triggered from preroll to play it or video
      FlixUtils.setCookie('from_preroll', 1 / 2, 1);
      // FlixFpi.triggerFpiIfEnabled();
      removePrerollAndShowVideo(true);
    };

    /**
     * Player event triggered when video ends
     */
    var onEnded = function onEnded() {
      FlixUtils.setCookie('from_preroll', 1 / 2, 1);
      //we set autoPlay to false to force user to click play and trigger FPI
      removePrerollAndShowVideo(false);
      //set FPI enabled after preroll ended
      document.addEventListener('click', FlixFpi.playerTriggerFPI, true);

      // Duplicate ProgressEvents.complete()
      sendUrlEventsOnce(Vast.config.trackingLinks.progressUrls.complete);
    };

    /**
     * Player event triggered periodically during play
     */
    var onTimeupdate = function onTimeupdate() {
      // Check video time and tracking urls and send progress
      // events if necessary
      ProgressEvents.check();

      // Stop preroll if it runs longer than parsed duration in config
      if (player.currentTime >= Vast.config.duration) {
        onEnded();
      }
    };

    /**
     * Destroy preroll player and show normal video player instead
     * @param {string} error 
     */
    Events.onError = function (error) {
      if (retry < max_retries) {
        FlixPreroll.show(autoPlay);
        retry++;
        return;
      }
      removePrerollAndShowVideo(autoPlay);
      FlixStats.increment('ad_error_preroll');

      // FlixErrorLogger.log('[Preroll error] ' + error);

      if (Flix.isLocal()) {
        console.error('[Preroll error] ' + error);
      }
    };

    /**
     * Show Skip button and Ad link
     */
    function setupSkipButton() {
      var _document$querySelect4, _document$querySelect5;
      // If already setup
      if (skipButtonInterval) {
        return;
      }
      (_document$querySelect4 = document.querySelector('#prerollAdLink')) === null || _document$querySelect4 === void 0 || _document$querySelect4.classList.remove('d-none');
      (_document$querySelect5 = document.querySelector('#prerollAdSkip')) === null || _document$querySelect5 === void 0 || _document$querySelect5.classList.remove('d-none');
      if (Vast.config.textButton) {
        var _document$querySelect6;
        (_document$querySelect6 = document.querySelector('#prerollAdCta')) === null || _document$querySelect6 === void 0 || _document$querySelect6.classList.remove('d-none');
        document.querySelector('#prerollAdCta span').innerHTML = Vast.config.textButton;
      }
      // document.querySelector('#visit-ad-site')?.classList.remove('d-none');

      document.querySelector('#prerollAdSkip').innerHTML = "Skip Ad in ".concat(Vast.config.skipOffset);
      var adPlayTime = 0;
      skipButtonInterval = setInterval(function () {
        /**
        * Check that #prerollAdSkip exists because when preroll still playing and you click
        * on another video this event might tick even though this HTML already destroyed
        */
        if (!document.querySelector('#prerollAdSkip')) {
          clearInterval(skipButtonInterval);
          return;
        }
        ++adPlayTime;

        // Enable Skip Ad button after skipOffset second
        if (!skipActivated) {
          var diffSkip = parseInt(Vast.config.skipOffset - adPlayTime);
          if (diffSkip <= 0) {
            // Show preroll only once
            setStopCookie();
            skipActivated = true;
            document.querySelector('#prerollAdSkip').innerHTML = "Skip Ad >>>";
            document.querySelector('#prerollAdSkip').addEventListener('click', onSkipClick);
          } else {
            document.querySelector('#prerollAdSkip').innerHTML = "Skip Ad in ".concat(diffSkip);
          }
        }

        // Check video time and tracking urls and send progress
        // events if necessary
        ProgressEvents.check();

        // Stop preroll if it runs longer than parsed duration in config
        if (adPlayTime >= Vast.config.duration) {
          onEnded();
        }
      }, 1000);
    }

    /**
     * Send get requests to the array of urls and EMPTY this array
     * @param {array} urls 
     */
    var sendUrlEventsOnce = function sendUrlEventsOnce(urls) {
      while (urls.length) {
        FlixUtils.request(urls.shift(), {}, true)["catch"](function (err) {
          return FlixStats.increment('ad_preroll_event_request_error');
        });
      }
    };

    /**
     * Sent events based on the video progress
     * @see Vast.config.trackingLinks.progressUrls
     */
    var ProgressEvents = function () {
      var ProgressEvents = {};
      ProgressEvents.check = function () {
        start();
        firstQuartile();
        midpoint();
        thirdQuartile();
        complete();
        progress();
      };
      var start = function start() {
        if (player.currentTime <= 1) {
          sendUrlEventsOnce(Vast.config.trackingLinks.progressUrls.start);
        }
      };
      var firstQuartile = function firstQuartile() {
        if (player.currentTime >= Math.ceil(Vast.config.duration * 0.25)) {
          sendUrlEventsOnce(Vast.config.trackingLinks.progressUrls.firstQuartile);
        }
      };
      var midpoint = function midpoint() {
        if (player.currentTime >= Math.ceil(Vast.config.duration * 0.5)) {
          sendUrlEventsOnce(Vast.config.trackingLinks.progressUrls.midpoint);
        }
      };
      var thirdQuartile = function thirdQuartile() {
        if (player.currentTime >= Math.ceil(Vast.config.duration * 0.75)) {
          sendUrlEventsOnce(Vast.config.trackingLinks.progressUrls.thirdQuartile);
        }
      };
      var complete = function complete() {
        if (player.currentTime >= Vast.config.duration) {
          sendUrlEventsOnce(Vast.config.trackingLinks.progressUrls.complete);
        }
      };

      /**
       * Custom timestamp progress events
       */
      var progress = function progress() {
        var ar = Vast.config.trackingLinks.progressUrls.progress;
        for (var i = 0; i < ar.length; i++) {
          var el = ar[i];
          if (player.currentTime >= el.time) {
            // Send event
            FlixUtils.request(el.url)["catch"](function (err) {
              return FlixStats.increment('ad_preroll_event_request_error');
            });

            // Remove element
            ar.splice(i, 1);
          }
        }
      };
      return ProgressEvents;
    }();
    return Events;
  }();

  /**
   * VAST configuration. `prepare()` will fetch, parse and setup config
   */
  var Vast = function () {
    var Vast = {};
    var cascadeSchemaLevels = 0;
    var cascadeSchemaLevelsMax = 4;

    /**
     * Used to reset config before every preroll
     * @returns Object
     */
    var defaultConfig = function defaultConfig() {
      return {
        videoUrl: '',
        clickThroughUrl: '',
        brandName: '',
        duration: 20,
        skipOffset: 5,
        trackingLinks: {
          progressUrls: {
            start: [],
            firstQuartile: [],
            midpoint: [],
            thirdQuartile: [],
            complete: [],
            progress: []
          },
          clickTrackingUrl: [],
          impressionUrl: []
        },
        textButton: ''
      };
    };

    /**
     * VAST resulting config
     */
    Vast.config = defaultConfig();

    /**
     * Prepare VAST config by getting XML from url and parsing it to "config"
     * @param {string} url 
     * @returns {Promise}
     */
    Vast.prepare = function (url) {
      // Reset config
      Vast.config = defaultConfig();
      cascadeSchemaLevels = 0;
      return new Promise(function (resolve, reject) {
        FlixUtils.request(url, {
          responseType: 'document',
          timeout: 5000
        }, true).then(Parser.parse).then(resolve)["catch"](reject);
      });
    };

    /**
     * VAST XML Parser
     */
    var Parser = function () {
      var Parser = {};

      /**
       * Parse VAST XML and set vide url, click link, tracking events, etc
       * @param {Document} xml VAST XML
       */
      Parser.parse = function (xml) {
        return new Promise(function (resolve, reject) {
          setGeneralVastConfigData(xml);
          setTrackingLinks(xml);
          if (cascadeSchemaLevels > cascadeSchemaLevelsMax) {
            reject('Maximum cascade levels reached');
          }
          if (!xml.querySelector('InLine') && xml.querySelector('VASTAdTagURI')) {
            // If it's just a wrapper, then another request should be made
            cascadeSchemaLevels++;
            FlixUtils.request(xml.querySelector('VASTAdTagURI').textContent, {
              responseType: 'document'
            }, true).then(Parser.parse).then(resolve)["catch"](reject);
          } else if (!Vast.config.videoUrl || !Vast.config.clickThroughUrl) {
            reject('Cannot get required VAST params');
          } else {
            resolve();
          }
        });
      };

      /**
       * Set videoUrl, brandName, duration, etc
       * @param {Document} xml
       */
      var setGeneralVastConfigData = function setGeneralVastConfigData(xml) {
        var _xml$querySelectorAll, _xml$querySelector, _xml$querySelector2, _ref, _ref2, _xml$querySelector3, _xml$querySelector4, _xml$querySelector5, _xml$querySelector6, _xml$querySelector7;
        /** Preroll video file link */
        var lastUsed = '';
        (_xml$querySelectorAll = xml.querySelectorAll('MediaFile')) === null || _xml$querySelectorAll === void 0 || _xml$querySelectorAll.forEach(function (elem) {
          if (elem.getAttribute('type') == 'video/mp4') {
            Vast.config.videoUrl = elem.textContent;
            Vast.config.videoType = 'mp4';
            return;
          } else {
            lastUsed = elem.textContent;
          }
        });
        if (!Vast.config.videoUrl) {
          Vast.config.videoUrl = lastUsed;
          Vast.config.videoType = 'streaming';
        }
        // Vast.config.videoUrl = xml.querySelector('MediaFile')?.get;
        Vast.config.videoDelivery = (_xml$querySelector = xml.querySelector('MediaFile')) === null || _xml$querySelector === void 0 ? void 0 : _xml$querySelector.getAttribute('delivery');

        /** Ad target link */
        Vast.config.clickThroughUrl = (_xml$querySelector2 = xml.querySelector('ClickThrough')) === null || _xml$querySelector2 === void 0 ? void 0 : _xml$querySelector2.textContent;

        /** Ad company name */
        Vast.config.brandName = (_ref = (_ref2 = ((_xml$querySelector3 = xml.querySelector('AdTitle')) === null || _xml$querySelector3 === void 0 ? void 0 : _xml$querySelector3.textContent) || null) !== null && _ref2 !== void 0 ? _ref2 : ((_xml$querySelector4 = xml.querySelector('IconClickThrough')) === null || _xml$querySelector4 === void 0 ? void 0 : _xml$querySelector4.textContent) || null) !== null && _ref !== void 0 ? _ref : ((_xml$querySelector5 = xml.querySelector('AdSystem')) === null || _xml$querySelector5 === void 0 ? void 0 : _xml$querySelector5.textContent) || null;

        /** Set duration if it's greater than 0. Otherwise keep default 20 sec */
        Vast.config.duration = hhmmss_to_seconds((_xml$querySelector6 = xml.querySelector('Duration')) === null || _xml$querySelector6 === void 0 ? void 0 : _xml$querySelector6.textContent) || 20;

        /** Set skip offset if it's greater than 0. Otherwise keep default 5 sec */
        Vast.config.skipOffset = hhmmss_to_seconds((_xml$querySelector7 = xml.querySelector('Linear')) === null || _xml$querySelector7 === void 0 ? void 0 : _xml$querySelector7.getAttribute('skipoffset')) || 5;
      };

      /**
       * @param {Document} xml
       */
      var setTrackingLinks = function setTrackingLinks(xml) {
        // Impression links
        for (var x = 0; x < xml.getElementsByTagName('Impression').length; x++) {
          Vast.config.trackingLinks.impressionUrl.push(xml.getElementsByTagName('Impression')[x].textContent);
          var _url = xml.getElementsByTagName('Impression')[x].textContent;
          if (_url) {
            var _pams = new URLSearchParams(_url);
            if (_pams) {
              var _ctaButton = _pams.get('keyText');
              if (_ctaButton) {
                Vast.config.textButton = _ctaButton.substring(0, 21); //limit to 21 chars
              }
            }
          }
        }

        // Click tracking links
        for (var _x = 0; _x < xml.getElementsByTagName('ClickTracking').length; _x++) {
          Vast.config.trackingLinks.clickTrackingUrl.push(xml.getElementsByTagName('ClickTracking')[_x].textContent);
        }

        // Progress tracking links
        if (xml.getElementsByTagName('TrackingEvents').length) {
          var trackingEvents = xml.getElementsByTagName('TrackingEvents')[0].getElementsByTagName('Tracking');
          for (var _x2 = 0; _x2 < trackingEvents.length; _x2++) {
            var elem = trackingEvents[_x2];
            switch (elem.getAttribute('event')) {
              case 'start':
                Vast.config.trackingLinks.progressUrls.start.push(elem.textContent);
                break;
              case 'firstQuartile':
                Vast.config.trackingLinks.progressUrls.firstQuartile.push(elem.textContent);
                break;
              case 'midpoint':
                Vast.config.trackingLinks.progressUrls.midpoint.push(elem.textContent);
                break;
              case 'thirdQuartile':
                Vast.config.trackingLinks.progressUrls.thirdQuartile.push(elem.textContent);
                break;
              case 'complete':
                Vast.config.trackingLinks.progressUrls.complete.push(elem.textContent);
                break;
              case 'progress':
                Vast.config.trackingLinks.progressUrls.progress.push({
                  time: hhmmss_to_seconds(elem.getAttribute('offset')),
                  url: elem.textContent
                });
                break;
              default:
                break;
            }
          }
        }
      };

      /**
       * Helper function to convert string duration to seconds.
       * Return `returnOnError` value if input string is invalid
       * 
       * @param {string} string "00:02:17" or "00:02:17.253"
       * @param {number} returnOnError
       * @returns {number}
       */
      var hhmmss_to_seconds = function hhmmss_to_seconds(string) {
        var returnOnError = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
        try {
          var result = parseInt(string.split('.')[0].split(':').reduce(function (acc, time) {
            return 60 * acc + +time;
          }));
          return isNaN(result) ? returnOnError : result;
        } catch (error) {
          return returnOnError;
        }
      };
      return Parser;
    }();
    return Vast;
  }();
  return FlixPreroll;
}();